import React, { use, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { GET_ALL_TIMESHEETS } from "../../graphql/timesheet";
import { useQuery } from "@apollo/client/react";
import Loading from "../../components/Loading";
import TimesheetList from "../../components/TimeSheetList";
import EmptyScreen from "../../components/EmptyScreen";
import { useMissionsTimesheetNotification } from "../../hooks/push-notification";
import { TimeSheetState } from "../../__generated__/graphql";
import { isLiftTimesheetSource } from "../../utils";
import NoTimesheetImage from "../../assets/images/no-timesheet-image.svg";
import ScreenCard from "../ScreenCard";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import { FIND_MY_MISSIONS } from "../../graphql/home";

const TimeSheetListScreen = () => {
    const i18n = useI18n();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { data: missionsData, refetch: refetchMissions } = useQuery(FIND_MY_MISSIONS, {
        variables: {},
        fetchPolicy: "cache-and-network",
    });
    const allMissions = useMemo(
        () => missionsData?.findMyMissions.elements || [],
        [missionsData?.findMyMissions.elements],
    );

    const {
        data: timeSheetsData,
        refetch: refetchTimeSheets,
        loading: timeSheetsLoading,
        error: timeSheetsError,
    } = useQuery(GET_ALL_TIMESHEETS, {
        variables: {},
        fetchPolicy: "cache-and-network",
    });
    const allTimeSheets = useMemo(
        () => timeSheetsData?.getAllTimeSheets.elements || [],
        [timeSheetsData?.getAllTimeSheets.elements],
    );

    const count = useMemo(
        () => timeSheetsData?.getAllTimeSheets.count || 0,
        [timeSheetsData?.getAllTimeSheets.count],
    );

    const hasPendingTimeSheet = allTimeSheets.some((ts) => ts.status === TimeSheetState.Open);

    useMissionsTimesheetNotification(hasPendingTimeSheet);

    const isInitialLoading = timeSheetsLoading && !timeSheetsData;

    const refetchAll = async () => {
        await Promise.all([refetchMissions(), refetchTimeSheets()]);
    };

    return (
        <ScreenCard
            refresh={refetchAll}
            isContentContainerFlex={count === 0 || isInitialLoading}
            headband={
                timeSheetsError
                    ? buildErrorHeadband(i18n.t("screens.user.timesheets.cannot_load_timesheets"))
                    : undefined
            }
        >
            {isInitialLoading ? (
                <Loading />
            ) : count > 0 && profileType ? (
                <TimesheetList profileType={profileType} timeSheets={allTimeSheets} />
            ) : (
                <EmptyScreen
                    image={NoTimesheetImage}
                    text={
                        allMissions.length > 0 &&
                        allMissions.every((m) => !isLiftTimesheetSource(m.payrollSource))
                            ? i18n.t("time_sheet.empty_external_ts")
                            : i18n.t("time_sheet.empty")
                    }
                />
            )}
        </ScreenCard>
    );
};

export default TimeSheetListScreen;
